import type { ComputedRef, InjectionKey } from 'vue'
import { gql, type ResultOf } from '~/tada/gql'

export type GroupTypeMeta = {
  value: string
  title: string
  icon: string
  color: string
  description: string
  /** Films come from FiveFest submissions instead of Film Challenge teams. */
  fiveFest?: boolean
  /** Role (without the year) whose users can be assigned to groups of this type. */
  role?: string
  /** Members rank the films and mark their ballot submitted. */
  ballots?: boolean
  /** Members join on their own (e.g. by submitting a ballot). */
  selfServe?: boolean
  /** Every submitted film is expected to land in at least one group. */
  partition?: boolean
  /** A film should never be in more than one group of this type. */
  exclusive?: boolean
  /** The group's results are tied to an award. */
  award?: boolean
  /** The group's results are tied to a FiveFest genre. */
  genre?: boolean
}

export const GROUP_TYPES: GroupTypeMeta[] = [
  {
    value: 'selection',
    title: 'Selection Committee',
    icon: 'mdi-gavel',
    color: 'deep-purple-lighten-2',
    description: 'Committee members review and vote on a set of films.',
    role: 'Selection Committee',
    partition: true,
    exclusive: true,
  },
  {
    value: 'prize',
    title: 'Prize',
    icon: 'mdi-gift-outline',
    color: 'amber',
    description: 'Prize sponsors pick a winner from the films in the group.',
    role: 'Prize Sponsor',
  },
  {
    value: 'judge',
    title: 'International Jury',
    icon: 'mdi-earth',
    color: 'light-blue',
    description: 'Judges rank the films for a single award.',
    role: 'International Judge',
    ballots: true,
    award: true,
  },
  {
    value: 'screening',
    title: 'Online Screening',
    icon: 'mdi-television-play',
    color: 'teal-lighten-1',
    description: 'A screening block shown together online.',
    partition: true,
  },
  {
    value: 'fivefest',
    title: 'FiveFest Academy',
    icon: 'mdi-filmstrip',
    color: 'pink-lighten-2',
    description:
      'A flight of FiveFest films. Academy members join by submitting a ballot.',
    fiveFest: true,
    selfServe: true,
    ballots: true,
    partition: true,
    genre: true,
  },
]

export const groupTypeMeta = (type: string): GroupTypeMeta =>
  GROUP_TYPES.find(t => t.value === type) || {
    value: type,
    title: type,
    icon: 'mdi-folder-outline',
    color: 'grey',
    description: '',
  }

const ReorderTeams =
  gql(`mutation GroupManagerReorderTeams($updates: [film_group_films_updates!]!) {
  update_film_group_films_many(updates: $updates) {
    affected_rows
  }
}`)

const ReorderSubmissions =
  gql(`mutation GroupManagerReorderSubmissions($updates: [film_group_festival_submissions_updates!]!) {
  update_film_group_festival_submissions_many(updates: $updates) {
    affected_rows
  }
}`)

const RemoveTeam =
  gql(`mutation GroupManagerRemoveTeam($id: Int!, $updates: [film_group_films_updates!]!) {
  delete_film_group_films_by_pk(id: $id) {
    id
  }
  update_film_group_films_many(updates: $updates) {
    affected_rows
  }
}`)

const RemoveSubmission =
  gql(`mutation GroupManagerRemoveSubmission($id: Int!, $updates: [film_group_festival_submissions_updates!]!) {
  delete_film_group_festival_submissions_by_pk(id: $id) {
    id
  }
  update_film_group_festival_submissions_many(updates: $updates) {
    affected_rows
  }
}`)

const AddTeams =
  gql(`mutation GroupManagerAddTeams($objects: [film_group_films_insert_input!]!) {
  insert_film_group_films(objects: $objects) {
    affected_rows
  }
}`)

const AddSubmissions =
  gql(`mutation GroupManagerAddSubmissions($objects: [film_group_festival_submissions_insert_input!]!) {
  insert_film_group_festival_submissions(objects: $objects) {
    affected_rows
  }
}`)

/**
 * Sort is unique per group, so renumbering happens in two passes: park every
 * row on a negative sort, then assign 1..n. This also closes any gaps.
 */
const renumber = (entryIds: number[]) => [
  ...entryIds.map((id, i) => ({
    where: { id: { _eq: id } },
    _set: { sort: -(i + 1) },
  })),
  ...entryIds.map((id, i) => ({
    where: { id: { _eq: id } },
    _set: { sort: i + 1 },
  })),
]

/**
 * Membership mutations for a group's films, hiding the difference between
 * Film Challenge teams and FiveFest submissions.
 */
export const useGroupFilmMutations = () => {
  const options = { clientId: 'admin' }
  const { mutate: reorderTeams } = useMutation(ReorderTeams, options)
  const { mutate: reorderSubmissions } = useMutation(ReorderSubmissions, options)
  const { mutate: removeTeam } = useMutation(RemoveTeam, options)
  const { mutate: removeSubmission } = useMutation(RemoveSubmission, options)
  const { mutate: addTeams } = useMutation(AddTeams, options)
  const { mutate: addSubmissions } = useMutation(AddSubmissions, options)

  /** Persist a new order, given every entry id in the group. */
  const reorder = (fiveFest: boolean, entryIds: number[]) =>
    fiveFest
      ? reorderSubmissions({ updates: renumber(entryIds) })
      : reorderTeams({ updates: renumber(entryIds) })

  /** Remove one entry and renumber the ones that remain. */
  const remove = (fiveFest: boolean, entryId: number, remainingIds: number[]) =>
    fiveFest
      ? removeSubmission({ id: entryId, updates: renumber(remainingIds) })
      : removeTeam({ id: entryId, updates: renumber(remainingIds) })

  /** Append films to the end of a group. */
  const add = (
    fiveFest: boolean,
    filmGroupId: number,
    filmIds: number[],
    maxSort: number,
  ) =>
    fiveFest
      ? addSubmissions({
          objects: filmIds.map((id, i) => ({
            film_group_id: filmGroupId,
            festival_submission_id: id,
            sort: maxSort + i + 1,
          })),
        })
      : addTeams({
          objects: filmIds.map((id, i) => ({
            film_group_id: filmGroupId,
            team_id: id,
            sort: maxSort + i + 1,
          })),
        })

  return { reorder, remove, add }
}

export const GroupManagerQuery =
  gql(`query GroupManager($ffid: numeric!, $poster: numeric!, $ffidInt: Int!, $posterInt: Int!) {
  film_groups(where: {film_festival_id: {_eq: $ffid}}, order_by: {id: asc}) {
    id
    name
    type
    award_id
    festival_genre_id
    award {
      id
      name
    }
    festival_genre {
      id
      name
    }
    teams(order_by: {sort: asc}) {
      id
      sort
      team_id
    }
    festival_submissions(order_by: {sort: asc}) {
      id
      sort
      festival_submission_id
    }
    users(order_by: {id: asc}) {
      id
      film_group_id
      rankings
      updated_at
      submitted_at
      user {
        id
        name
        email
        sub
        picture
      }
    }
  }
  teams(where: {film_festival_id: {_eq: $ffid}, submitted_at: {_is_null: false}}, order_by: {film_name: asc}) {
    id
    team_number
    name
    film_name
    poster: assets(where: {asset_type_id: {_eq: $poster}}, order_by: {created: desc}, limit: 1) {
      id
      file_name
    }
  }
  festival_submissions(where: {film_festival_id: {_eq: $ffidInt}, submitted_at: {_is_null: false}}, order_by: {film_title: asc}) {
    id
    film_title
    genre {
      id
      name
    }
    poster: assets(where: {asset_type_id: {_eq: $posterInt}}, order_by: {created_at: desc}, limit: 1) {
      id
      file_name
    }
  }
  awards(order_by: {name: asc}) {
    id
    name
  }
  festival_genres(where: {film_festival_id: {_eq: $ffidInt}}, order_by: {name: asc}) {
    id
    name
  }
}`)

export type GroupManagerData = ResultOf<typeof GroupManagerQuery>
export type ManagedGroup = GroupManagerData['film_groups'][number]
export type ManagedMember = ManagedGroup['users'][number]

/** A Film Challenge team or a FiveFest submission. */
export type ManagedFilm = {
  key: string
  id: number
  fiveFest: boolean
  title: string
  subtitle: string
  search: string
  poster?: string
}

export const filmKey = (fiveFest: boolean, id: number) =>
  `${fiveFest ? 's' : 't'}${id}`

export const toManagedFilms = (data?: GroupManagerData): ManagedFilm[] => [
  ...(data?.teams || []).map(team => ({
    key: filmKey(false, team.id),
    id: team.id,
    fiveFest: false,
    title: team.film_name || team.name,
    subtitle: `#${team.team_number} · ${team.name}`,
    search: `${team.film_name} ${team.name} #${team.team_number} ${team.team_number}`,
    poster: team.poster[0]?.file_name,
  })),
  ...(data?.festival_submissions || []).map(s => ({
    key: filmKey(true, s.id),
    id: s.id,
    fiveFest: true,
    title: s.film_title,
    subtitle: s.genre?.name || '',
    search: `${s.film_title} ${s.genre?.name || ''}`,
    poster: s.poster[0]?.file_name,
  })),
]

/** A group's entries in order, whichever kind of film it holds. */
export const groupEntries = (group: ManagedGroup) =>
  groupTypeMeta(group.type).fiveFest
    ? group.festival_submissions.map(e => ({
        id: e.id,
        sort: e.sort,
        filmKey: filmKey(true, e.festival_submission_id),
      }))
    : group.teams.map(e => ({
        id: e.id,
        sort: e.sort,
        filmKey: filmKey(false, e.team_id),
      }))

export const GROUP_MANAGER_PATH = '/admin/group-manager'

export const groupPath = (id: number, tab?: 'people') =>
  `${GROUP_MANAGER_PATH}/${id}${tab ? `/${tab}` : ''}`

/** Shared by the Group Manager parent page and its nested routes. */
export type GroupManagerContext = {
  groups: ComputedRef<ManagedGroup[]>
  films: ComputedRef<ManagedFilm[]>
  loaded: ComputedRef<boolean>
  refetch: () => unknown
  create: (type?: string) => void
  edit: (group: ManagedGroup) => void
  remove: (group: ManagedGroup) => void
}

export const GroupManagerKey: InjectionKey<GroupManagerContext> =
  Symbol('GroupManager')

export const useGroupManager = () => {
  const context = inject(GroupManagerKey)
  if (!context) throw new Error('useGroupManager must be used inside the Group Manager page')
  return context
}
